import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import AuthModal from './AuthModal';
import { authFetch } from '../utils/authFetch';
import { artBtnNavCta, artBtnNavPill } from '../ui/artmosphereButtonTheme';

export default function AuthAwareNavbar({ homePremium = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  
  // Determine button text based on user type
  const getMentorButtonText = () => {
    if (user?.accountType === 'mentor') {
      // Check if organization user (don't require isPrimaryId)
      if (user?.mentorType === 'organization') {
        return 'Organization Dashboard';
      }
      return 'Mentors Corner';
    }
    return 'Artist Corner';
  };

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAccountDropdown && !event.target.closest('.account-dropdown')) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountDropdown]);

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  const handleMyGalleryClick = async () => {
    if (isAuthenticated) {
      console.log('AuthAwareNavbar - User data for Artist Corner:', {
        accountType: user?.accountType,
        mentorType: user?.mentorType,
        isPrimaryId: user?.isPrimaryId,
        email: user?.email,
        fullUser: user
      });
      
      // Route based on user account type
      if (user?.accountType === 'mentor') {
        // Check if this is an organization user (even if isPrimaryId is not set, check mentorType)
        if (user?.mentorType === 'organization') {
          // Organization user - check if primary ID or regular org user
          // Check membership status before navigating
          console.log('AuthAwareNavbar - Organization user detected, checking membership status...');
          
          try {
            const membershipResponse = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`);
            if (membershipResponse.ok) {
              const membershipData = await membershipResponse.json();
              const membership = membershipData.membership || {};
              const isActive = membership.isActive;
              const isExpired = membership.isExpired;
              
              console.log('AuthAwareNavbar - Organization membership check:', {
                isActive,
                isExpired,
                membership,
                isPrimaryId: user?.isPrimaryId
              });
              
              // Navigate to organization dashboard if active, or renewal if expired
              if (isActive && !isExpired) {
                console.log('AuthAwareNavbar - Organization membership is active, navigating to organization dashboard');
                navigate("/organization-dashboard");
              } else {
                console.log('AuthAwareNavbar - Organization membership is expired, navigating to renewal');
                navigate("/organization-renewal");
              }
            } else {
              // If API fails, still try to navigate to organization dashboard
              console.log('AuthAwareNavbar - Failed to fetch membership status, navigating to organization dashboard');
              navigate("/organization-dashboard");
            }
          } catch (error) {
            console.error('AuthAwareNavbar - Error checking membership status:', error);
            // On error, navigate to organization dashboard
            navigate("/organization-dashboard");
          }
        } else if (user?.mentorType === 'single' || user?.mentorType === 'mentor') {
          // Single mentor user - go to mentor corner dashboard
          console.log('AuthAwareNavbar - Single mentor user detected, navigating to mentor dashboard');
          navigate("/mentor-dashboard");
        } else {
          // Fallback: Check if user might be organization user by checking if they have org-related fields
          // If no clear type, default to mentor dashboard but log warning
          console.warn('AuthAwareNavbar - Mentor account without clear type, defaulting to mentor dashboard', {
            accountType: user?.accountType,
            mentorType: user?.mentorType,
            isPrimaryId: user?.isPrimaryId
          });
          navigate("/mentor-dashboard");
        }
      } else {
        // Student/parent users - go to My Art World
        console.log('AuthAwareNavbar - Student/parent user detected, navigating to My Art World');
        navigate("/my-art-world");
      }
    } else {
      setShowAuthOptions(true);
    }
  };

  const handleAuthChoice = (mode) => {
    setAuthMode(mode);
    setShowAuthOptions(false);
    setAuthModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleModeSwitch = (newMode) => {
    setAuthMode(newMode);
  };

  return (
    <>
      {/* Modern translucent navbar */}
      <nav
        className={`sticky top-0 z-[70] relative ${homePremium ? 'home-premium-nav' : ''}`}
        style={{
          paddingTop: 'max(8px, min(calc(8px + env(safe-area-inset-top, 0px)), 30px))',
        }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div
            className={
              homePremium
                ? 'home-nav-shell px-3 sm:px-6 h-12 sm:h-14 flex items-center justify-between'
                : 'rounded-2xl bg-white/40 backdrop-blur-md border border-amber-200/60 shadow px-3 sm:px-6 h-12 sm:h-14 flex items-center justify-between'
            }
          >
            {/* Brand */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-95 transition"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#FFE9B8] via-[#F5D99B] to-[#E8C97A] text-[#5C4428] font-bold shadow-sm text-sm sm:text-base border border-[#E8C97A]/80">
                A
              </span>
              <span
                className={
                  homePremium
                    ? 'home-nav-brand font-semibold text-base sm:text-lg tracking-wide'
                    : 'text-black font-semibold text-base sm:text-lg tracking-wide'
                }
              >
                Artmosphere
              </span>
            </button>

            {/* Right controls */}
            <div className="hidden sm:flex space-x-2 md:space-x-3 items-center text-xs md:text-sm">
              {/* Authentication-aware buttons */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-2 md:space-x-3">
                  {/* Hi, [Name] pill */}
                  <span
                    className={`${artBtnNavPill} px-3 md:px-4 py-1.5 md:py-2 rounded-full font-medium text-xs md:text-sm`}
                  >
                    Hi, {user?.displayName?.split(' ')[0] || 'Artist'}
                  </span>

                  <button
                    onClick={handleMyGalleryClick}
                    className={`${artBtnNavCta} px-3 md:px-5 py-1.5 md:py-2 rounded-full font-semibold flex items-center gap-1 text-xs md:text-sm`}
                  >
                    {getMentorButtonText()}
                    <span className="text-xs md:text-sm">⌄</span>
                  </button>
                  
                  {/* Logout - Icon only */}
                  <button
                    onClick={handleLogout}
                    className={`${artBtnNavPill} p-1.5 md:p-2 rounded-full transition`}
                    title="Logout"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 md:space-x-4">
                  <button
                    onClick={() => handleAuthClick('login')}
                    className={`${artBtnNavPill} px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium`}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className={`${artBtnNavCta} px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold`}
                  >
                    Join Community
                  </button>
                </div>
              )}
            </div>

            <div className="sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-black text-xl"
              >
                ☰
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden mt-2 space-y-2 text-amber-800 bg-white/90 backdrop-blur-sm border border-white/30 rounded-xl p-3">
              {/* Single entry point to mentor dashboard handled by Artist Corner button below */}
              <button 
                onClick={() => navigate("/artists")} 
                className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors"
              >
                Artists Profiles
              </button>
              <button 
                onClick={() => navigate("/offerings")} 
                className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors"
              >
                Upcoming Offerings
              </button>
              <a 
                href="https://www.artmosphere.info/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors"
              >
                Artmosphere Overview
              </a>
              
              {/* Mobile authentication buttons */}
              {isAuthenticated ? (
                <>
                  <div className="px-2 py-1 text-sm font-medium text-amber-800">
                    Hi, {user?.displayName?.split(' ')[0] || 'Artist'}
                  </div>
                  <button 
                    onClick={handleMyGalleryClick} 
                    className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors font-medium"
                  >
                    {getMentorButtonText()}
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => handleAuthClick('login')} 
                    className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors"
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => handleAuthClick('signup')} 
                    className="block w-full text-left px-2 py-1 hover:bg-amber-50 rounded transition-colors font-medium"
                  >
                    Join Community
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Auth Options Modal */}
      {showAuthOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-xl border-2 border-amber-200 max-w-md w-full">
            <h2 className="text-3xl font-bold text-amber-800 mb-4 text-center">
              Join the Artmosphere Community!
            </h2>
            <p className="text-amber-700 mb-6 text-center">
              Create, share, and grow with thousands of young artists from around the world!
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleAuthChoice('signup')}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-500 transition-all duration-200 shadow-md font-medium"
              >
                Start Your Artist Journey
              </button>
              <button
                onClick={() => handleAuthChoice('login')}
                className="w-full border-2 border-amber-300 text-amber-700 py-3 px-6 rounded-lg hover:bg-amber-50 transition-all duration-200 font-medium"
              >
                Welcome Back
              </button>
            </div>
            <p className="text-sm text-amber-600 mt-4 text-center">
              Join our community of creative young artists!
            </p>
            <button
              onClick={() => setShowAuthOptions(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        mode={authMode}
        onModeSwitch={handleModeSwitch}
        redirectSignupToFullPage
      />
    </>
  );
} 
